/**
 * Static constants and shared types for the App Map graph view.
 */

import type { LucideIcon } from 'lucide-react';
import {
  Code2,
  Hammer,
  FlaskConical,
  GitBranch,
  Package as PackageIcon,
  Activity,
  ShieldCheck,
  Rocket,
} from 'lucide-react';
import type { KBNode, DetectedTool, SdlcStage } from '../types';

export const STAGE_ICONS: Record<SdlcStage, LucideIcon> = {
  source: Code2,
  build: Hammer,
  test: FlaskConical,
  ci: GitBranch,
  package: PackageIcon,
  observe: Activity,
  security: ShieldCheck,
  deploy: Rocket,
};

// Edge types shown by default at overview level. Everything else surfaces
// only when a node is focused.
export const OVERVIEW_EDGE_TYPES = new Set(['conflicts_with', 'depends_on']);

// Restrained two-tone palette. Conflicts warn (rose), everything else reads as
// neutral "related" (slate) — intensity communicates emphasis via opacity,
// not hue, so the graph reads as documentation rather than decoration.
export const EDGE_STYLE: Record<string, { color: string; dash?: string; weight: number }> = {
  depends_on: { color: '#94a3b8', weight: 1.5 },
  pairs_with: { color: '#64748b', weight: 1 },
  conflicts_with: { color: '#f87171', dash: '4,3', weight: 2 },
  alternative_to: { color: '#94a3b8', dash: '6,4', weight: 1 },
  extends: { color: '#94a3b8', dash: '8,4', weight: 1 },
};

export const EDGE_LABEL: Record<string, string> = {
  depends_on: 'depends on',
  pairs_with: 'pairs with',
  conflicts_with: 'conflicts',
  alternative_to: 'alternative',
  extends: 'extends',
};

export interface ColumnData {
  stage: SdlcStage;
  tools: Array<{ node: KBNode; det: DetectedTool }>;
  gaps: Array<{ node: KBNode; priority?: string; reason: string }>;
}
